import React, { useState, useEffect } from 'react';
import { Button, Label, Input, Dropdown, Option } from '@fluentui/react-components';
import { Dismiss24Regular, Save20Regular } from '@fluentui/react-icons';
import ResourcesAccountChatComponent from '../ResourcesAccountChatComponent';
import GreetingChatComponent from '../GreetingChatComponent';
import RoutingChatComponent from '../RoutingChatComponent';
import QueueSelectorComponent from '../../GenericComponent/QueueSelectorComponent';
import './CSS/EditNodePanel.css';

/**
 * EditNodePanel - Panel d'édition à droite pour modifier les nœuds
 */
const EditNodePanel = ({ 
  node, 
  isOpen, 
  onClose, 
  onSave, 
  chatFlowData, 
  setChatFlowData, 
  st_CallFlows,
  ACTIVE_TENANT,
  SCOPES,
  onDeleteNode
}) => {
  const [localChatFlowData, setLocalChatFlowData] = useState(chatFlowData);
  const [panelWidth, setPanelWidth] = useState(450);
  const [isResizing, setIsResizing] = useState(false);

  const isRoutingNode = node?.id?.startsWith('routing-');
  const isQueueNode = node?.id?.startsWith('queue-');

  // Gestion du redimensionnement
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      const newWidth = window.innerWidth - e.clientX;
      if (newWidth >= 350 && newWidth <= 800) {
        setPanelWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // Synchroniser avec chatFlowData
  useEffect(() => {
    if (chatFlowData) {
      setLocalChatFlowData(chatFlowData);
    }
  }, [chatFlowData]);

  // Log when localChatFlowData changes
  useEffect(() => {
  }, [localChatFlowData]);

  const handleSave = () => {
    if (onSave) {
      onSave(localChatFlowData);
    }
  };

  const handleDeleteNode = () => {
    if (onDeleteNode) {
      onDeleteNode(node.id);
    }
    onClose();
  };

  if (!isOpen || !node) return null;

  const renderPanelContent = () => {
    if (node.id === 'chatflow-start') {
      return (
        <ResourcesAccountChatComponent
          chatFlowData={localChatFlowData}
          setChatFlowData={setLocalChatFlowData}
          isEditMode={true}
          permissions={localChatFlowData?.permissions}
          st_CallFlows={st_CallFlows}
          SCOPES={SCOPES}
          isInDiagramPanel={true}
        />
      );
    }

    if (node.id === 'greeting') {
      return (
        <GreetingChatComponent
          chatFlowData={localChatFlowData}
          setChatFlowData={setLocalChatFlowData}
          isEditMode={true}
          permissions={localChatFlowData?.permissions}
          isInDiagramPanel={true}
        />
      );
    }

    if (isRoutingNode) {
      const routingKey = node.id.replace('routing-', '');
      return (
        <RoutingChatComponent
          chatFlowData={localChatFlowData}
          setChatFlowData={setLocalChatFlowData}
          isEditMode={true}
          ACTIVE_TENANT={ACTIVE_TENANT}
          permissions={localChatFlowData?.permissions}
          selectedRouting={routingKey}
          isInDiagramPanel={true}
        />
      );
    }

    if (isQueueNode) {
      // Édition d'un nœud queue temporaire (créé dans customerChoiceActions)
      const queueId = node.id.replace('queue-', '');
      
      // Trouver la queue dans les routings
      let queueDisplayName = 'Queue';
      let routingName = '';
      Object.entries(localChatFlowData.routing || {}).forEach(([key, routing]) => {
        if (routing.customerChoiceActions) {
          Object.values(routing.customerChoiceActions).forEach(action => {
            if (action.next?.queueId === queueId) {
              queueDisplayName = action.next.queueDisplayName || 'Queue';
              routingName = action.next.routingName || '';
            }
          });
        }
        if (routing.next?.queueId === queueId) {
          queueDisplayName = routing.next.queueDisplayName || 'Queue';
          routingName = routing.next.routingName || '';
        }
      });

      return (
        <div style={{ padding: '20px' }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <Label>Nom de la queue</Label>
            <Input
              value={queueDisplayName}
              disabled
              style={{ width: '100%', marginTop: '0.5rem', backgroundColor: '#f5f5f5' }}
            />
          </div>
          
          {routingName && (
            <div style={{ marginBottom: '1.5rem' }}>
              <Label>Nom du routing</Label>
              <Input
                value={routingName === "0" ? "start" : routingName}
                disabled
                style={{ width: '100%', marginTop: '0.5rem', backgroundColor: '#f5f5f5' }}
              />
            </div>
          )}
          
          <div style={{ marginBottom: '1.5rem' }}>
            <Label>Type de routage</Label>
            <Dropdown
              value="queue"
              selectedOptions={['queue']}
              disabled
              style={{ width: '100%', marginTop: '0.5rem' }}
            >
              <Option value="queue">queue</Option>
            </Dropdown>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <Label>Sélectionner une queue</Label>
            <QueueSelectorComponent
              displayName={queueDisplayName}
              getData={(queueData) => {
                // Mettre à jour la queue dans tous les routings qui l'utilisent
                const updatedRouting = { ...localChatFlowData.routing };
                let updated = false;

                Object.keys(updatedRouting).forEach(key => {
                  const routing = updatedRouting[key];
                  
                  // Vérifier dans customerChoiceActions
                  if (routing.customerChoiceActions) {
                    Object.keys(routing.customerChoiceActions).forEach(actionKey => {
                      const action = routing.customerChoiceActions[actionKey];
                      if (action.next?.queueId === queueId) {
                        updatedRouting[key].customerChoiceActions[actionKey].next = {
                          ...action.next,
                          queueId: queueData.id,
                          queueDisplayName: queueData.displayName
                        };
                        updated = true;
                      }
                    });
                  }
                  
                  // Vérifier dans next
                  if (routing.next?.queueId === queueId) {
                    updatedRouting[key].next = {
                      ...routing.next,
                      queueId: queueData.id,
                      queueDisplayName: queueData.displayName
                    };
                    updated = true;
                  }
                });

                if (updated) {
                  setLocalChatFlowData({ ...localChatFlowData, routing: updatedRouting });
                }
              }}
              ACTIVE_TENANT={ACTIVE_TENANT}
            />
          </div>
        </div>
      );
    }

    return <div style={{ padding: '20px' }}>Nœud non reconnu</div>;
  };

  const getNodeTitle = () => {
    if (node.id === 'chatflow-start') return 'Resource Account';
    if (node.id === 'greeting') return 'Greeting Messages';
    if (isQueueNode) return 'Queue';
    if (isRoutingNode) {
      const routingKey = node.id.replace('routing-', '');
      const routing = chatFlowData?.routing?.[routingKey];
      return routing?.displayName || `Routing ${routingKey === "0" ? "Start" : routingKey}`;
    }
    return 'Éditer le nœud';
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className={`edit-panel-overlay ${isOpen ? 'visible' : ''}`}
        onClick={onClose}
      />

      {/* Panel */}
      <div 
        className={`edit-node-panel ${isOpen ? 'open' : ''}`}
        style={{ width: `${panelWidth}px` }}
      >
        {/* Resize handle */}
        <div
          className="panel-resize-handle"
          onMouseDown={() => setIsResizing(true)}
        />

        {/* Header */}
        <div className="panel-header">
          <div className="panel-header-content">
            <h3 className="panel-title">{getNodeTitle()}</h3>
            <div className="panel-header-actions">
              <Button
                appearance="primary"
                onClick={handleSave}
                icon={<Save20Regular />}
              >
                Enregistrer
              </Button>
              <Button
                icon={<Dismiss24Regular />}
                appearance="subtle"
                onClick={onClose}
                className="panel-close-btn"
                aria-label="Fermer"
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="panel-content">
          {renderPanelContent()}
        </div>
      </div>
    </>
  );
};

export default EditNodePanel;
